import { Container } from 'react-bootstrap';
import PageSectionTitle from '../Layout/PageSectionTitle';
import Equation from '../Layout/Equation';
import MathJax from 'react-mathjax';
import "./EstimandsTextContent.css";

const EstimandsTextContent = (props) => {
  return(
    <div id="estimands-text-content">
      <Container fluid className="con-no-pad">
        <MathJax.Provider>
          <section id="section0">
            <p>
              The estimand is the average effect of the intervention on monthly spending per patient, for treated patients in the 
              study. Specifically, the quantities of interest are sample average treatment effects on the treated (SATTs) for a 
              number of different groupings of treated patients.
            </p>
            <p>
              We define causal effects in terms of the potential outcomes framework. 
              Let <MathJax.Node inline formula={"Y_{it}(1)"} /> and <MathJax.Node inline formula={"Y_{it}(0)"} /> denote the 
              potential outcomes for patient <i>i</i> at time <i>t</i> were patient <i>i</i> to receive the treatment or 
              control condition respectively at time <i>t</i>. The observed outcome for patient <i>i</i> is given by <MathJax.Node 
                inline formula={"Y_{it} = z_i p_t Y_{it} (1)+(1 - z_i p_t ) Y_{it}(0)"} />, where <MathJax.Node 
                inline formula={"z_i"} /> indicates membership in the treatment group and <MathJax.Node 
                inline formula={"p_t"} /> is an indicator for <span className="var-text">post</span>, equal to 1 if time <i>t</i> is in the intervention period;  
              that is, <MathJax.Node inline formula={"Y_{it} = Y_{it}(1)"} /> if <i>i</i> receives the treatment at time <i>t</i> and <MathJax.Node
                inline formula={"Y_{it} = Y_{it}(0)"} /> if unit <i>i</i> receives the control condition at 
              time <i>t</i>. The treatment effect for unit <i>i</i> at each time <i>t</i> in the intervention period is defined 
              as <MathJax.Node inline formula={"Y_{it}(1) - Y_{it}(0)"} />. In practice, only one of the potential 
              outcomes is observed for each patient at each time point, and the counterfactual outcome must be 
              estimated. 
            </p>
            <p>
              The average treatment effect on the treated (at time <i>t</i> in the intervention period) is 
              defined as 
            </p>
            <Equation tex="ATT(t) = E_{(i \mid z_i = 1)}(Y_{it}(1) - Y_{it}(0))" />
          </section>

          <section id="section1">
            <PageSectionTitle>
              The overall SATT
            </PageSectionTitle>
            <p>
              Our target estimand is the sample average treatment effect on the treated, meaning we take an average over 
              observed units rather than an expectation over a broader population. In particular we are targeting the SATT 
              averaged over the two post-intervention years: 
            </p>
            <Equation tex="SATT = \frac{1}{\sum_{t=3}^4 N_t} \sum_{t=3}^4 \sum_{i: z_i = 1} Y_{it}(1) - Y_{it}(0)" />
            <p>
              Here <MathJax.Node inline formula={"N_t"} /> denotes the total number of patients in the treatment 
              group observed during year <i>t</i>. Note that not all patients are observed in all years, so in 
              general <MathJax.Node inline formula={"N_3 \\neq N_4"} />. 
            </p>
            <p>
              In the practice-level track, we still target the patient-level SATT, so the SATT will be a sample size-weighted 
              average across practices (here <MathJax.Node inline formula={"Y_{jt}(z)"} /> denotes the average over patients in 
              practice <i>j</i> at time <i>t</i> of <MathJax.Node inline formula={"Y_{it}(z), z_j"} /> is the treatment assignment 
              of practice <i>j</i>, and <MathJax.Node inline formula={"n_{jt}"} /> is the number of patients in practice <i>j</i> at 
              time <i>t</i>):
            </p>
            <Equation tex="SATT = \frac{1}{\sum_{t=3}^4 N_t} \sum_{t=3}^4 \sum_{j: z_j = 1} n_{jt}(Y_{jt}(1) - Y_{jt}(0)) " />
            <span>
              Note that here <MathJax.Node inline formula={"N_t=∑_j n_{jt}"} />.
            </span>
          </section>

          <section id="section2">
            <PageSectionTitle>
              SATT by year
            </PageSectionTitle>
            <span>In addition to the overall SATT, we also target the SATT by year (one each for Years 3 and 4):</span>
            <Equation tex="SATT_t = \frac{1}{N_t} \sum_{i: z_i = 1} Y_{it}(1) - Y_{it}(0)" />
            <p>
              <MathJax.Node inline formula={"SATT_3"} /> reflects the impact of being exposed to the intervention for one year, since 
              treated practices begin receiving the intervention at the beginning of Year 3. However, recall that treated practices 
              continue receiving the intervention through the end of Year 4. Therefore, impacts might not emerge until Year 4, when 
              practices have had further opportunity to benefit from the intervention: <MathJax.Node inline formula={"SATT_4"} /> reflects 
              these long-term effects.
            </p>
          </section>

          <section id="section3">
            <PageSectionTitle>
              SATT by subgroup
            </PageSectionTitle>
            <span>
              We also ask for estimates of the subgroup-specific SATT for various subgroups of interest, averaged over the two-year 
              period during which the intervention is implemented. In particular, we target the SATT for subgroups defined by each level of 
              the five categorical and binary variables <span className="var-text">X1, …, X5</span>. For example, 
              because <span className="var-text">X2</span> takes on the values <i>A, B, C</i>, we are 
              interested in conditional SATTs for the subgroups defined by <span className="var-text">X2=A, X2=B, X2=C</span>, 
              where the SATT for subgroup <i>S</i> is given by:
            </span>
            <Equation tex="SATT(S) = \frac{1}{∑_{t=3}^4 N_t(S)} \sum_{t=3}^4 \sum_{i: z_i = 1, i \in S} Y_{it}(1) - Y_{it}(0)" />
          </section>

          <section id="section4">
            <PageSectionTitle>
              SATT by practice (optional)
            </PageSectionTitle>
            <span>
              For methods that can estimate individual-level treatment effects, we ask for practice-specific 
              treatment effect estimates for each treated practice in the dataset, averaged over the two-year period during 
              which the intervention is implemented. Using the notation from the equation for subgroup SATTs, the SATT for 
              a given practice <i>P</i> is <i>SATT(P)</i>.
            </span>
          </section>
        </MathJax.Provider>
      </Container>
    </div>
  )
}

export default EstimandsTextContent;
